import ManageMedia from '~/api/manage_media';
import DynamicWebsites from '~/api/dynamic_websites';
import Media from '~/api/media';
import { Message, MessageBox } from 'element-ui';
import { mapGetters } from 'vuex';
import documentUtils from '~/components/grapesjs/utils/document_utils';
import cssjs from 'jotform-css.js';
import { getPreviewContent } from '../../services/preview_service';
import LiquidService from '~/services/liquid';
import GrapeUtils from '~/components/grapesjs/utils/utils';
import {
    getZoomWrapperContents,
    scaledContainerId,
    zoomContainerId
}  from '~/bundles/media/services/zoom_service';

// timeout is for editable_text_fill js that also uses a timeout
function textFillWait() {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            resolve();
        }, 1100);
    });
}

function removeTop(dom, selector) {
    const el = dom.querySelector('.te-top-vars');

    if (!el) {
        return '';
    }

    return el.parentNode.removeChild(el).outerHTML;
}

export default {
    async updateMediaTemplateAndPreview({state, commit, rootState, dispatch, getters}) {
        commit('updateIsSaving', true);

        commit('media/preview/updatePreviewData', {
            // we need to switch to the html view in order to get the contents of the iframe
            type: 'html',
            html: '',
            content: state.formData.content,
            media_id: state.formData.id
        }, {root: true});

        try {
            const preview = await dispatch('media/preview/getMediaPreviewPromise', null,  {root: true});
            const media = await ManageMedia.updateMediaTemplate(getters.saveData);
            const textFill = await textFillWait();
            const publish = await dispatch('publishAssets');
            const form = await dispatch('updateMediaForm');
        } catch (error) {
            commit('media/preview/triggerError', error, {root: true});
            throw new Error(error);
        }

        commit('updateHasChanges', false);

        if (rootState.grapeEditor != null) {
            commit('templateCreator/updateInitHtml', rootState.grapeEditor.grapeEditor.getOriginalHtml(), {root: true});
        }

        commit('updateIsSaving', false);
    },

    async publishAssets({state, commit, rootState, dispatch}) {
        commit('media/preview/updatePreviewData', {
            html: getPreviewContent(),
        }, {root: true});

        await dispatch('media/preview/updatePreviewAssets', null, {root: true}).then(response => {
            commit('updateIsSaving', false);
            $(document).trigger("assetsPublished");
        }).catch(() => {});
    },

    updateMediaTemplate({state, commit, rootState}, showMessage = true) {
        commit('updateIsSaving', true);

        ManageMedia.updateMediaTemplate(state.formData).then(response => {
            commit('updateEditorDialogOpen', false);
            commit('updateHasChanges', false);
            commit('templateCreator/updateInitHtml', rootState.grapeEditor.grapeEditor.getOriginalHtml(), {root: true});
            if (showMessage) {
                Message.success(response.msg);
            }
        }).catch(error => {
            Message.error(error.msg);
        }).finally(() => {
            commit('updateIsSaving', false);
        });
    },

    getMediaAssets({state, commit}) {
        commit('updateLoading', {
            imageGallery: true
        });

        Media.getMediaAssets(state.formData.id).then(response => {
            commit('updateImages', response.assets.images);
        }).catch(() => {
            Message.error('Images could not be loaded');
        }).finally(() => {
            commit('updateLoading', {
                imageGallery: false
            });
        });
    },

    async cloneAsset({state, commit}) {
        try {
            commit('updateIsSaving', true);
            return await ManageMedia.cloneAsset(state.cloneFormData);
        } catch (error) {
            Message.error('Media piece could not be duplicated');
        } finally {
            commit('updateIsSaving', false);
        }
    },

    async getMediaForm({state, commit, dispatch, rootState, getters}) {
        commit('updateLoading', {
            form: true
        });
        commit('media/settings/updateIsLoadingMediaForm', true, {root: true});

        try {
            // TODO: refactor this so we have 1 source of truth for the media html,
            // currently its in media.media & media.creator
            const media = await ManageMedia.getMediaForm(rootState.media.media.media_id);

            commit('media/updateMedia', media.media, {root: true});
            commit('media/updateMedia', { markup_image_url: media.markup_image_url }, {root: true});
            commit('media/updateInitMedia', media.media, {root: true});
            commit('media/updateAllowedComarketerUserTypes', media, {root: true});
            commit('media/updateTags', media, {root: true});
            commit('media/updateFolders', media, {root: true});
            commit('media/settings/updateSettings', media, {root: true});

            commit('oembed/updateUrl', media.media.video_link, {root: true});

            const html = await dispatch('addBrandingSnippet', media.media.html);
            dispatch('validateEmptyState', html);
            dispatch('contentPresets/getPresets', media.media.content_id, {root: true});
            commit('updateFormData', {
                content: media.media.html,
                id: media.media.media_id,
                contentId: media.media.content_id,
                website_id: media.media.website_id
            });

            if (html) {
                commit('templateCreator/setBody', html, {root: true});
            }

            return media;
        } catch (error) {
            Message.error(error.message);
        } finally {
            commit('updateLoading', {
                form: false
            });
            commit('media/settings/updateIsLoadingMediaForm', false, {root: true});
        }
    },

    async addBrandingSnippet({}, html) {
        if (!html) {
            return ''
        }

        const hasBrandSnippet = LiquidService.matchIncludeName(html, 'expert_content_branding');
        const snippet = `{% include 'expert_content_branding' %}`;

        let dom = documentUtils.stringToDom(html);
        const topVars = dom.querySelector('.te-top-vars');

        if (!topVars) {
            html = `
                <div class="te-top-vars">
                    ${snippet}
                </div>

                ${html}
            `;
        } else if (topVars && !hasBrandSnippet) {
            topVars.insertBefore(document.createTextNode(snippet), topVars.firstChild);
            html = dom.querySelector('html').outerHTML;
        }

        return html;
    },

    async getTemplates({commit}, params) {
        try {
            const templates = await ManageMedia.getTemplates(params);
            commit('media/settings/updateTemplates', templates.data, {root: true});
        } catch (error) {
            Message.error(error.message);
        }
    },

    async getAutoPayOptions({rootState}) {
        try {
            let payload = {
                user_id: rootState.media.media.org_cost_share_user_id,
                cost_share: rootState.media.media.org_cost_share_percent
            };
            return await ManageMedia.getAutoPayOptions(payload);
        } catch (error) {
            Message.error(error.message);
        }
    },

    async getOrgMediaData({commit, rootState}) {
        try {
            let payload = {
                org_id: rootState.media.media.org_id,
                media_id: rootState.media.media.media_id
            };
            const media = await ManageMedia.getOrgMediaData(payload);
            commit('media/updateTags', media, {root: true});
            commit('media/updateFolders', media, {root: true});
            commit('media/settings/updateApprovalFlows', media, {root: true});

            return media;
        } catch (error) {
            Message.error(error.message);
        }
    },

    async getOrgMediaForm({rootState}) {
        try {
            return await ManageMedia.getMediaForm(rootState.media.media.media_id);
        } catch (error) {
            Message.error(error.message);
        }
    },

    async updateMediaForm({commit, rootState}) {
        try {
            commit('updateIsSaving', true);
            const media = await ManageMedia.updateMediaForm(rootState.media.media);
            commit('media/updateInitMedia', rootState.media.media, {root: true});
            return media;
        } catch (error) {
            Message.error(error.message);
        } finally {
            commit('updateIsSaving', false);
        }
    },

    async updateContent({commit, dispatch, rootState, getters, state}, options = {}) {

        if (!state.formData.content) {
            commit('updateFormData', {
                content: state.formData.content
            });
        }

        if (!getters.showCanvas) {
            return state.formData.content;
        }

        const parser = new cssjs.cssjs();
        let baseCss = rootState.templateCreator.parsedCss;
        let newCss = parser.parseCSS(rootState.grapeEditor.grapeEditor.getCss());

        newCss = parser.deleteBySelector(newCss, scaledContainerId);
        newCss = parser.deleteBySelector(newCss, '#zoom-container');

        parser.intelligentMerge(baseCss, newCss);

        baseCss = [...rootState.templateCreator.parsedPrintCss, ...baseCss];

        // bring the spcial css attribute like `te-color` back to `color`
        // this is due to the cmyk values being removed
        documentUtils.iterateCss(baseCss, rule => {
            const dir = rule.directive;

            if (dir.slice(0, 3) == 'te-') {
                rule.directive = dir.slice(3, dir.length);
            }
        });

        const html = getZoomWrapperContents(
            rootState.grapeEditor.grapeEditor.getOriginalHtml()
        );

        if (!html) {
            return html;
        }

        let dom = documentUtils.stringToDom(html);
        dom = documentUtils.removeAllAttr(dom, ['data-mce-style', 'data-highlightable', 'data-gjs-type']);

        if (getters.isInMillimeters) {
            baseCss = documentUtils.pxToMm(baseCss);
        }

        const topVars = removeTop(dom, '.te-top-vars');
        const inlined = GrapeUtils.inlineIdStyles(
            dom.querySelector('body'),
            dom.querySelector('body').innerHTML,
            parser.getCSSForEditor(baseCss)
        );

        // remove id css selectors
        baseCss = baseCss.filter(item => {
            return item.selector.indexOf('#') !== 0;
        });

        const content = documentUtils.replaceStyles(
            inlined,
            parser.getCSSForEditor(baseCss),
            topVars
        );

        commit('updateFormData', {
            content: content
        })

        return content;
    },

    async saveAndRefreshMarkupImage({commit, dispatch, rootState}) {
        commit('silentOpenEditorDialog');
        commit('media/preview/updateIsLoading', true, {root: true});

        try {
            await dispatch('updateMediaTemplateAndPreview');
            commit('templateCreator/setSpecialCss', null, {root: true});
            commit('media/preview/updateIsLoading', false, {root: true});
            commit('silentCloseEditorDialog');
        } catch(error) {
            commit('media/preview/updateIsLoading', false, {root: true});
            commit('silentCloseEditorDialog');
            throw new Error(error);
        }
    },

    addComarketingBlock({commit, rootState}, el) {
        const selector = '[data-comarketable="true"]';

        if (el.matches(selector) || el.querySelector(selector)) {
            commit('updateComarketableContent', true);
            commit('media/updateMedia', {
                is_comarketable: '1'
            }, {root: true});
        } else {
            commit('updateComarketableContent', false);
        }
    },

    removeComarketingBlock({commit, rootState}, el) {
        const selector = '[data-comarketable="true"]';

        if (!rootState.media.initMedia.is_comarketable && !el.matches(selector) && !el.querySelector(selector)) {
            commit('updateComarketableContent', false);
            commit('media/updateMedia', {
                is_comarketable: '0'
            }, {root: true});
        }
    },

    validateEmptyState({commit}, html) {
        if (!html) {
            commit('updateHasPage', false);
            commit('updateHasSectionContainer', false);
            return false;
        }

        const dom = documentUtils.stringToDom(html);
        const page = dom.querySelector('.te-page-container > .te-page');
        const sectionContainer = dom.querySelector('.te-section-container');

        if (page) {
            commit('updateHasPage', true);
        } else {
            commit('updateHasPage', false);
        }

        if (sectionContainer) {
            commit('updateHasSectionContainer', true);
        } else {
            commit('updateHasSectionContainer', false);
        }
    },

    openEditor({commit, dispatch}) {
        setTimeout(() => {

            dispatch('updateContent').then(response => {
                commit('updateEditorDialogOpen', true);
            }).catch(error => {
                console.error(error);
            });
        });
    },

    async unpublish({commit, dispatch}) {
        commit('media/updateMedia', {
            status: '0'
        }, {root: true});

        await dispatch('updateMediaForm').then(response => {
            $(document).trigger("assetsPublished");
        });
    }
};




